import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const galleryImages = [
  { src: 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?q=80&w=2070&auto=format&fit=crop', alt: 'Parkplatz Übersicht', category: 'parkplatz' },
  { src: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=2070&auto=format&fit=crop', alt: 'Valet Service', category: 'valet' },
  { src: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2069&auto=format&fit=crop', alt: 'Shuttle Service', category: 'shuttle' },
  { src: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop', alt: 'Flughafen Düsseldorf', category: 'flughafen' },
  { src: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=2071&auto=format&fit=crop', alt: 'Autopflege Service', category: 'autopflege' },
  { src: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=2070&auto=format&fit=crop', alt: 'Premium Fahrzeuge', category: 'fahrzeuge' },
  { src: 'https://images.unsplash.com/photo-1556388158-158ea5ccacbd?q=80&w=2070&auto=format&fit=crop', alt: 'Terminal Ankunft', category: 'terminal' },
  { src: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=1974&auto=format&fit=crop', alt: 'Unser Team', category: 'team' },
  { src: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop', alt: 'Kundenservice', category: 'team' },
];

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const { t } = useLanguage();

  const openLightbox = (index) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="overflow-hidden pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.gallery.subtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t.gallery.title1}{' '}
              <span className="text-[#D4AF37]">{t.gallery.title2}</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              {t.gallery.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group cursor-pointer overflow-hidden rounded-2xl"
                onClick={() => openLightbox(index)}
                data-testid={`gallery-image-${index}`}
              >
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div>
                      <span className="text-[#D4AF37] text-xs font-medium uppercase tracking-wider">
                        {t.gallery.categories[image.category]}
                      </span>
                      <h3 className="text-white text-lg font-semibold mt-1">{image.alt}</h3>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              {t.gallery.ctaTitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
              {t.gallery.ctaDesc}
            </p>
            <a
              href="https://mcdgroup.de/buchungssystem/index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
              data-testid="gallery-book-btn"
            >
              {t.gallery.ctaBtn}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-3 text-white/70 hover:text-white transition-colors z-50"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 p-3 text-white/70 hover:text-white transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 p-3 text-white/70 hover:text-white transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <div className="max-w-5xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-wider">
                {t.gallery.categories[galleryImages[selectedImage].category]}
              </span>
              <h3 className="text-white text-xl font-semibold mt-1">
                {galleryImages[selectedImage].alt}
              </h3>
              <p className="text-white/50 text-sm mt-2">
                {selectedImage + 1} / {galleryImages.length}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
